import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NotificationChannel } from "../../proto/generated/common";
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { selectMultiPaneFullScreen } from "../../selectors/common";
import { useDispatch, useSelector } from "react-redux";
import { LOGOS_BASE_URL, MULTIPANE_FULLSCREEN_MODE_ID } from "../../assets/constants";
import AlertSettingsTab from "./alertSettingsTab/alertSettingsTab";
import {addUserAlert, AlertModalPurpose, removeAlertModalData, updateUserAlert} from "../../reducer/alerts.reducer";
import {selectAlertModalData} from "../../selectors/alerts";
import {createAlert} from "../../services/apis/alerts";
import { getCreateUserAlertRequestObject, hasUsableTopLevelAlertSecurity } from "../../util/alerts";
import { getSecurityAsync, getSecurityKey } from "../../util/security";
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import ClearIcon from "../../assets/icons/ClearIcon";
import {ExtraSmallButton} from "../custom";
import ArrowBackIcon from "../../assets/icons/ArrowBackIcon";
import type { AlertSettingsSubView } from "./alertModal.typings";
import { clearConditionalEntryAlertModalHandlers, invokeConditionalEntryApply, invokeConditionalEntryCancel } from "../../util/conditionalEntryAlertModalBridge";
import { isConditionalEntryReady, rewriteConditionalLhsSecurityForUnderlyingChartRef } from "../../util/conditionalEntry";

function conditionalRuleModalTitle(purpose: AlertModalPurpose): string {
    switch (purpose) {
        case 'conditionalExit':
            return 'CONDITIONAL EXIT'
        case 'conditionalModify':
            return 'MODIFY CONDITION'
        case 'conditionalEntry':
            return 'CONDITIONAL ENTRY'
    }
}

const AlertModal: React.FC = () => {

    const dispatch = useDispatch();
    const container = useSelector(selectMultiPaneFullScreen)
    const { chartSnapshot, alert, edit, purpose, conditionalModalChartRef, conditionalModalChartVariants } =
        useSelector(selectAlertModalData) ?? {}
    const isConditionalRuleModal =
        purpose === 'conditionalEntry' ||
        purpose === 'conditionalExit' ||
        purpose === 'conditionalModify'
    const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([NotificationChannel.DESKTOP_POPUP])
    const [activeTab, setActiveTab] = useState('settings')
    const [settingsSubView, setSettingsSubView] = useState<AlertSettingsSubView>(null)
    const [error, setError] = useState(false)
    const dialogContentRef = useRef<HTMLDivElement>(null)
    const [settingsContentMinHeight, setSettingsContentMinHeight] = useState<number | undefined>(undefined)
    const [resolvedHeaderSecurity, setResolvedHeaderSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)

    const headerSecurityKey = useMemo(() => {
        const top = alert?.security
        if (hasUsableTopLevelAlertSecurity(top)) {
            return getSecurityKey(top!.exchange, top!.code)
        }
        const s0 = chartSnapshot?.securityList?.[0]
        if (s0) return getSecurityKey(s0.exchange, s0.code)
        return ''
    }, [alert?.security, chartSnapshot?.securityList])

    useEffect(() => {
        if (!headerSecurityKey) {
            setResolvedHeaderSecurity(undefined)
            return
        }
        let cancelled = false
        getSecurityAsync(headerSecurityKey).then((s) => {
            if (!cancelled) setResolvedHeaderSecurity(s)
        })
        return () => {
            cancelled = true
        }
    }, [headerSecurityKey])

    const primarySymbolDisplayName = useMemo(() => {
        const r = resolvedHeaderSecurity
        if (r) return r.displayName || r.symbol || r.code || ''
        const top = alert?.security
        if (hasUsableTopLevelAlertSecurity(top)) {
            return top!.code || ''
        }
        const primarySecurity = chartSnapshot?.securityList?.[0] as any
        if (!primarySecurity) return ''
        return primarySecurity.displayName || primarySecurity.code || ''
    }, [resolvedHeaderSecurity, alert?.security, chartSnapshot])

    const primarySecurityLogo = useMemo(() => {
        const logoUrl = resolvedHeaderSecurity?.logoUrl
        if (!logoUrl) return ''
        return `${LOGOS_BASE_URL}${logoUrl}.svg`
    }, [resolvedHeaderSecurity])

    const handleTabChange = (_event: React.ChangeEvent<{}>, newTab: string) => {
        setActiveTab(newTab)
        if (newTab !== 'settings') {
            setSettingsSubView(null)
        }
    }

    const settingsSubViewTitle = useMemo(() => {
        if (!settingsSubView) return null
        if (settingsSubView.kind === 'editCondition') return 'Edit condition'
        return settingsSubView.childPath === null ? 'Add condition' : 'Add nested condition'
    }, [settingsSubView])

    const exitSettingsSubView = useCallback(() => setSettingsSubView(null), [])

    const closeModal = useCallback(() => {
        if (
            purpose === 'conditionalEntry' ||
            purpose === 'conditionalExit' ||
            purpose === 'conditionalModify'
        ) {
            invokeConditionalEntryCancel()
        }
        clearConditionalEntryAlertModalHandlers()
        dispatch(removeAlertModalData())
    }, [dispatch, purpose])

    const createUserAlert = useCallback(async () => {
        if (!alert) return;
        const newAlert = await createAlert(getCreateUserAlertRequestObject(alert))
        if (edit) {
            dispatch(updateUserAlert(newAlert))
        } else {
            dispatch(addUserAlert(newAlert))
        }
        closeModal();
    }, [alert, edit, closeModal, dispatch])

    const handleConditionalApply = useCallback(() => {
        const draftAlert = alert
        if (!draftAlert?.condition) return
        const condition = rewriteConditionalLhsSecurityForUnderlyingChartRef(
            draftAlert.condition,
            conditionalModalChartRef,
            conditionalModalChartVariants
        )
        if (!isConditionalEntryReady({ enabled: true, condition })) return
        invokeConditionalEntryApply(condition)
        clearConditionalEntryAlertModalHandlers()
        dispatch(removeAlertModalData())
    }, [dispatch, alert, conditionalModalChartRef, conditionalModalChartVariants])

    const onValidation = useCallback((error: boolean) => setError(error), [])

    useEffect(() => {
        if (settingsSubView) return
        const el = dialogContentRef.current
        if (!el) return
        const update = () => {
            const h = Math.ceil(el.getBoundingClientRect().height)
            if (h > 0) setSettingsContentMinHeight(h)
        }
        update()
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [settingsSubView, activeTab])

    const dialogMaxWidth = settingsSubView && activeTab === 'settings' ? 'sm' : 'xs'

    const primaryDisabled = useMemo(() => {
        if (error) return true
        if (!isConditionalRuleModal || !alert?.condition) return false
        return !isConditionalEntryReady({ enabled: true, condition: alert.condition })
    }, [error, isConditionalRuleModal, alert?.condition])

    if (!alert) {
        return null
    }

    return <Dialog
        open
        onClose={closeModal}
        fullWidth
        maxWidth={dialogMaxWidth}
        container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
        aria-labelledby="alerts-dialog">
        <DraggableDialogTitle>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                {settingsSubView && activeTab === 'settings' ? (
                    <>
                        <Stack direction="row" alignItems="center" spacing={0.75} minWidth={0} flex={1}>
                            <IconButton
                                size="small"
                                onClick={exitSettingsSubView}
                                onMouseDown={(e) => e.stopPropagation()}
                                aria-label="Back to alert settings"
                                color="inherit"
                            >
                                <ArrowBackIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {settingsSubViewTitle}
                            </Typography>
                        </Stack>
                        <ExtraSmallButton onClick={closeModal} aria-label="close" color={"inherit"}>
                            <ClearIcon/>
                        </ExtraSmallButton>
                    </>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1.2} minWidth={0}>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {isConditionalRuleModal && purpose
                                    ? conditionalRuleModalTitle(purpose)
                                    : edit
                                      ? 'Edit alert on'
                                      : 'Create alert on'}
                            </Typography>
                            <Avatar
                                src={primarySecurityLogo || undefined}
                                sx={{ width: 18, height: 18, fontSize: 14 }}
                            >
                                {(primarySymbolDisplayName || 'S').charAt(0)}
                            </Avatar>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {primarySymbolDisplayName}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <ExtraSmallButton onClick={closeModal} aria-label="close" color={"inherit"}>
                                <ClearIcon/>
                            </ExtraSmallButton>
                        </Stack>
                    </>
                )}
            </Stack>
        </DraggableDialogTitle>
        <DialogContent
            ref={dialogContentRef}
            sx={
                settingsSubView && settingsContentMinHeight
                    ? { minHeight: settingsContentMinHeight }
                    : undefined
            }
        >
            {notificationChannels.length > 1 && !settingsSubView ?
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="inherit"
                    >
                        <Tab label="Settings" value="settings"/>
                        <Tab label="Notifications" value="notifications"/>
                    </Tabs>
                </Box>
                :
                <></>
            }
            {
                activeTab === "settings" && (
                    <AlertSettingsTab
                        onValidation={onValidation}
                        settingsSubView={settingsSubView}
                        onSettingsSubViewChange={setSettingsSubView}
                    />
                )
            }
        </DialogContent>
        <DialogActions
            sx={{
                visibility: settingsSubView ? 'hidden' : 'visible',
                pointerEvents: settingsSubView ? 'none' : 'auto'
            }}
            aria-hidden={settingsSubView ? true : undefined}
        >
            <Stack direction={'row'} alignItems={'right'} spacing={1}>
                <Button variant="outlined" onClick={closeModal}>
                    Cancel
                </Button>
                <Button autoFocus
                        disabled={primaryDisabled}
                        variant={'contained'}
                        data-event={isConditionalRuleModal ? 'Conditional entry condition applied' : `Alert ${edit ? 'Saved' : 'Created'}`}
                        data-event-alert-message={alert?.message}
                        data-event-alert-name={alert?.name}
                        onClick={isConditionalRuleModal ? handleConditionalApply : createUserAlert}
                >
                    {isConditionalRuleModal ? 'Apply' : (edit ? 'Save' : 'Create')}
                </Button>
            </Stack>
        </DialogActions>
    </Dialog>
}
export default memo(AlertModal);